import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button, Card, Chip, Empty } from "../components/ui";
import { Mail, UserPlus, X, Check, Clock, Send, Users, ArrowLeft, Lock, ArrowRight, MessageCircle, KeyRound } from "lucide-react";

export default function InvitationsPage() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { refreshMe } = useAuth();
  const [receivedInvites, setReceivedInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("received");
  const [sending, setSending] = useState(null);
  const [inviteForm, setInviteForm] = useState({ club_id: "", email: "", mobile: "", role: "member" });
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [clubs, setClubs] = useState([]);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitDetail, setLimitDetail] = useState(null);
  const [sentInvites, setSentInvites] = useState([]);
  const [lastSentInvite, setLastSentInvite] = useState(null);
  const [joinCode, setJoinCode] = useState("");
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    const acceptId = searchParams.get("accept");
    if (acceptId) {
      autoAccept(acceptId);
    } else {
      fetchData();
    }
  }, []);

  const autoAccept = async (inviteId) => {
    try {
      await api.post(`/clubs/invitations/${inviteId}/accept`);
      toast.success("Invitation accepted! Welcome to the club.");
      setSearchParams({});
      await refreshMe();
      await fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.upgrade_required) {
        setLimitDetail({
          message: detail.message || "Club is full",
          currentPlan: detail.current_plan || "your current plan",
          maxMembers: detail.max_members || 0,
          currentMembers: detail.current_members || 0,
        });
        setShowLimitModal(true);
      } else {
        toast.error(apiError(err));
      }
      setSearchParams({});
      await fetchData();
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [{ data: received }, { data: userClubs }, { data: sent }] = await Promise.all([
        api.get("/clubs/invitations"),
        api.get("/clubs/me"),
        api.get("/clubs/invitations/sent"),
      ]);
      setReceivedInvites(received);
      setClubs(userClubs.filter(c => c.my_role === "owner" || c.my_role === "admin"));
      setSentInvites(sent);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setSending(inviteForm.club_id);
    try {
      const { data } = await api.post("/clubs/invite", inviteForm);
      setLastSentInvite(data);
      toast.success("Invitation sent!");
      setInviteForm({ club_id: "", email: "", mobile: "", role: "member" });
      await fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.upgrade_required) {
        setLimitDetail({
          message: detail.message || "Club is full",
          currentPlan: detail.current_plan || "your current plan",
          maxMembers: detail.max_members || 0,
          currentMembers: detail.current_members || 0,
        });
        setShowLimitModal(true);
      } else {
        toast.error(apiError(err));
      }
    } finally {
      setSending(null);
    }
  };

  const shareViaWhatsApp = (invite) => {
    const clubName = clubs.find(c => c.id === invite.club_id)?.name || "a cricket club";
    const appUrl = window.location.origin;
    const text = `Hey! You've been invited to join *${clubName}* on Cricso!\n\nTap below to accept:\n${appUrl}/login?from=/invitations%3Faccept%3D${invite.id}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const joinWithCode = async (e) => {
    e.preventDefault();
    setJoining(true);
    try {
      const { data } = await api.post("/clubs/join", { join_code: joinCode.trim().toUpperCase() });
      toast.success(data.already_member ? `You're already in ${data.name} — switched to it` : `Joined ${data.name}!`);
      setJoinCode("");
      await refreshMe();
      nav("/home");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.upgrade_required) {
        setLimitDetail({
          message: detail.message || "Club is full",
          currentPlan: detail.current_plan || "your current plan",
          maxMembers: detail.max_members || 0,
          currentMembers: detail.current_members || 0,
        });
        setShowLimitModal(true);
      } else {
        toast.error(apiError(err));
      }
    } finally {
      setJoining(false);
    }
  };

  const acceptInvite = async (inviteId) => {
    try {
      await api.post(`/clubs/invitations/${inviteId}/accept`);
      toast.success("Invitation accepted!");
      await refreshMe();
      await fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.upgrade_required) {
        setLimitDetail({
          message: detail.message || "Club is full",
          currentPlan: detail.current_plan || "your current plan",
          maxMembers: detail.max_members || 0,
          currentMembers: detail.current_members || 0,
        });
        setShowLimitModal(true);
      } else {
        toast.error(apiError(err));
      }
    }
  };

  const declineInvite = async (inviteId) => {
    try {
      await api.post(`/clubs/invitations/${inviteId}/decline`);
      toast.success("Invitation declined");
      await fetchData();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (loading) {
    return (
      <div className="app-shell flex flex-col min-h-screen bg-white">
        <div className="flex-1 grid place-items-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand border-t-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell flex flex-col min-h-screen bg-white">
      <div className="px-5 pt-6 pb-4">
        <button onClick={() => nav(-1)} className="h-10 w-10 rounded-full hover:bg-black/5 grid place-items-center mb-4" data-testid="back-btn">
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-3xl font-bold">Club Invitations</h1>
        <p className="text-ink-muted mt-1">Manage and accept club invitations</p>
      </div>

      <div className="px-5 flex-1 overflow-y-auto pb-10">
        {/* Tab Switcher */}
        <div className="grid grid-cols-2 gap-1 p-1 bg-black/[0.04] rounded-2xl mb-6 max-w-md">
          <button
            onClick={() => setTab("received")}
            className={`h-11 rounded-xl text-sm font-semibold ${tab === "received" ? "bg-white shadow-sm text-ink" : "text-ink-muted"}`}
          >
            Received ({receivedInvites.length})
          </button>
          <button
            onClick={() => setTab("send")}
            className={`h-11 rounded-xl text-sm font-semibold ${tab === "send" ? "bg-white shadow-sm text-ink" : "text-ink-muted"}`}
          >
            Send Invite
          </button>
        </div>

        {tab === "received" && (
          <div className="space-y-4">
            <Card>
              <div className="flex items-center gap-2 mb-1">
                <KeyRound size={18} className="text-brand" />
                <h3 className="font-semibold text-lg">Join with club code</h3>
              </div>
              <p className="text-sm text-ink-muted mb-4">Got a code from a club owner? Enter it to join directly — no invitation needed.</p>
              <form onSubmit={joinWithCode} className="flex gap-2">
                <input
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  placeholder="e.g. AB12CD3"
                  maxLength={12}
                  autoCapitalize="characters"
                  autoComplete="off"
                  className="flex-1 min-w-0 h-12 px-4 rounded-2xl bg-white border border-black/10 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 placeholder:text-ink-muted/70 text-ink font-mono tracking-widest uppercase"
                  data-testid="join-code-input"
                />
                <Button type="submit" size="lg" disabled={joining || !joinCode.trim()} data-testid="join-code-btn">
                  {joining ? "Joining..." : "Join"}
                </Button>
              </form>
            </Card>

            {receivedInvites.length === 0 ? (
              <Empty
                title="No invitations"
                subtitle="Invitations from other clubs will appear here"
                icon={Mail}
              />
            ) : (
              receivedInvites.map((invite) => (
                <InvitationCard
                  key={invite.id}
                  invite={invite}
                  onAccept={acceptInvite}
                  onDecline={declineInvite}
                />
              ))
            )}
          </div>
        )}

        {tab === "send" && (
          <div className="space-y-6">
            {clubs.length === 0 ? (
              <Empty
                title="No clubs to invite to"
                subtitle="You need to be an owner or admin of a club to send invitations"
                icon={Users}
              />
            ) : (
              <>
                <Card>
                  <h3 className="font-semibold text-lg mb-4">Send New Invitation</h3>
                  <form onSubmit={handleInvite} className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <select
                        value={inviteForm.club_id}
                        onChange={(e) => setInviteForm(prev => ({ ...prev, club_id: e.target.value }))}
                        className="h-12 px-4 rounded-2xl bg-white border border-black/10 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 text-ink"
                        required
                      >
                        <option value="">Select Club</option>
                        {clubs.map((c) => (
                          <option key={c.id} value={c.id}>{c.name} ({c.my_role})</option>
                        ))}
                      </select>
                      <select
                        value={inviteForm.role}
                        onChange={(e) => setInviteForm(prev => ({ ...prev, role: e.target.value }))}
                        className="h-12 px-4 rounded-2xl bg-white border border-black/10 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 text-ink"
                      >
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <input
                        type="email"
                        placeholder="Email address"
                        value={inviteForm.email}
                        onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                        className="h-12 px-4 rounded-2xl bg-white border border-black/10 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 placeholder:text-ink-muted/70 text-ink"
                      />
                      <input
                        type="tel"
                        placeholder="Mobile number (optional)"
                        value={inviteForm.mobile}
                        onChange={(e) => setInviteForm(prev => ({ ...prev, mobile: e.target.value }))}
                        className="h-12 px-4 rounded-2xl bg-white border border-black/10 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 placeholder:text-ink-muted/70 text-ink"
                      />
                    </div>
                    <p className="text-xs text-ink-muted">The user must have a Cricso account. Provide email or mobile to find them.</p>
                    <Button type="submit" size="lg" className="w-full" disabled={sending}>
                      {sending ? "Sending..." : <><Send size={18} /> Send Invitation</>}
                    </Button>
                  </form>
                </Card>

                {lastSentInvite && (
                  <Card className="border-brand-200 bg-brand-50/50">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-ink">Invitation sent!</p>
                        <p className="text-xs text-ink-muted mt-0.5">Share the accept link via WhatsApp</p>
                      </div>
                      <Button variant="primary" size="sm" onClick={() => shareViaWhatsApp(lastSentInvite)} data-testid="share-whatsapp-last">
                        <MessageCircle size={16} /> Share
                      </Button>
                    </div>
                  </Card>
                )}

                {sentInvites.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-ink-muted mb-3">Sent Invitations</h3>
                    <div className="space-y-2">
                      {sentInvites.map((inv) => (
                        <Card key={inv.id} className="py-3 px-4">
                          <div className="flex items-center justify-between">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-ink truncate">{inv.invitee_name || inv.invitee_email || "Unknown"}</p>
                              <p className="text-xs text-ink-muted">{inv.club?.name || "Club"} · {inv.role} · {fmtDate(inv.created_at)}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 ml-3">
                              <Chip tone={inv.status === "accepted" ? "success" : inv.status === "declined" ? "danger" : inv.status === "expired" ? "default" : "warning"} className="text-xs capitalize">
                                {inv.status}
                              </Chip>
                              {inv.status === "pending" && (
                                <button onClick={() => shareViaWhatsApp(inv)} className="h-8 w-8 rounded-full bg-[#25D366] hover:bg-[#20BA5C] grid place-items-center transition-colors" data-testid={`share-whatsapp-${inv.id}`}>
                                  <MessageCircle size={16} className="text-white" />
                                </button>
                              )}
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                <Card>
                  <h3 className="font-semibold text-lg mb-4">How it works</h3>
                  <ul className="space-y-2 text-sm text-ink-muted">
                    <li className="flex items-center gap-2"><Check size={14} className="text-brand" /> Enter the recipient's email or mobile number</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-brand" /> They must have a Cricso account</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-brand" /> They'll receive a notification to accept/decline</li>
                    <li className="flex items-center gap-2"><Check size={14} className="text-brand" /> Once accepted, they become a member of your club</li>
                  </ul>
                </Card>
              </>
            )}
          </div>
        )}
      </div>

      {/* Club Full Modal */}
      {showLimitModal && limitDetail && (
        <div className="fixed inset-0 z-50" data-testid="club-full-modal-overlay">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowLimitModal(false)} />
          <div className="absolute inset-0 flex items-center justify-center p-5">
          <div className="bg-white rounded-3xl p-6 pb-8 w-full max-w-[400px] shadow-xl animate-in zoom-in-95 fade-in duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-ink">Club is Full</h3>
              <button onClick={() => setShowLimitModal(false)} className="h-9 w-9 rounded-full bg-black/5 hover:bg-black/10 grid place-items-center" aria-label="Close">
                <X size={20} className="text-ink-muted" />
              </button>
            </div>
            <div className="text-center mb-6">
              <div className="h-16 w-16 rounded-2xl bg-brand-100 grid place-items-center mx-auto mb-4">
                <Users size={32} className="text-brand" />
              </div>
              <p className="text-sm text-ink-muted mb-2">{limitDetail.message}</p>
              <p className="text-sm text-ink-muted">
                Current plan: <span className="font-semibold text-ink">{limitDetail.currentPlan}</span>
              </p>
              <p className="text-sm text-ink-muted mt-1">
                Members: <span className="font-mono font-bold text-ink">{limitDetail.currentMembers}/{limitDetail.maxMembers}</span>
              </p>
            </div>
            <div className="space-y-3">
              <Button variant="primary" size="lg" className="w-full" onClick={() => { setShowLimitModal(false); nav("/pricing", { replace: true }); }} data-testid="club-full-view-plans">
                <ArrowRight size={20} /> View Plans
              </Button>
              <Button variant="outline" size="lg" className="w-full" onClick={() => setShowLimitModal(false)} data-testid="club-full-cancel">
                OK
              </Button>
            </div>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InvitationCard({ invite, onAccept, onDecline }) {
  const club = invite.club;
  const inviter = invite.inviter_name;
  const expired = new Date(invite.expires_at) < new Date();

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 flex-1">
          <div className="h-12 w-12 rounded-xl bg-brand-100 grid place-items-center flex-shrink-0">
            {club?.logo ? (
              <img src={club.logo} alt={club.name} className="h-10 w-10 rounded-lg object-cover" />
            ) : (
              <Users size={24} className="text-brand" />
            )}
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-lg truncate">{club?.name || "Club"}</h3>
            <p className="text-sm text-ink-muted mt-0.5">
              Invited by {inviter} · {invite.role === "admin" ? "Admin" : "Member"} role
            </p>
            <p className="text-xs text-ink-muted mt-1">
              Expires {fmtDate(invite.expires_at)} · {expired ? "Expired" : "Pending"}
            </p>
          </div>
        </div>
        {!expired && invite.status === "pending" && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={() => onDecline(invite.id)} data-testid={`decline-invite-${invite.id}`}>
              <X size={16} /> Decline
            </Button>
            <Button variant="primary" size="sm" onClick={() => onAccept(invite.id)} data-testid={`accept-invite-${invite.id}`}>
              <Check size={16} /> Accept
            </Button>
          </div>
        )}
        {invite.status === "accepted" && (
          <Chip tone="success" className="text-xs"><Check size={10} /> Accepted</Chip>
        )}
        {invite.status === "declined" && (
          <Chip tone="danger" className="text-xs"><X size={10} /> Declined</Chip>
        )}
        {expired && invite.status === "pending" && (
          <Chip tone="default" className="text-xs"><Clock size={10} /> Expired</Chip>
        )}
      </div>
    </Card>
  );
}
